/**
 * 一覧表の見出しのメニュー(2026-10-14 発注者指示「エクセルのフィルタのように」)。DataTable の headerMenu で使う。
 * 並べ替え(昇順・降順・解除)と、その列での絞り込み:
 *  - values: 値の一覧にチェック(検索・すべて選択)。チェックを外した値の行を隠す
 *  - range: 下限・上限(数値)。空白(「—」)の行を含めるか。列に区分(filterGroup)があれば、その区分のチェックも並べる
 * 業務ロジックは持たない(何の値で絞るかは列の filterValue が決める)。
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconSortAsc, IconSortDesc } from '@core/components/icons';

export type ColumnFilterState =
  | { kind: 'values'; excluded: string[] }
  | { kind: 'range'; min: number | null; max: number | null; includeBlank: boolean; excludedGroups?: string[] };

export const BLANK_KEY = '';

type ValueOption = { key: string; label: string; count: number };

const MENU_W = 260;

export default function DataTableColumnMenu({
  anchor,
  title,
  sortable,
  sortDir,
  onSort,
  filterKind,
  options,
  state,
  onFilter,
  onClose,
}: {
  anchor: HTMLElement;
  title: string;
  sortable: boolean;
  sortDir: 'asc' | 'desc' | null;
  onSort: (dir: 'asc' | 'desc' | null) => void;
  filterKind: 'values' | 'range' | null;
  /** values のときの選択肢、range のときは区分の選択肢(値・表示・件数) */
  options: ValueOption[];
  state: ColumnFilterState | null;
  onFilter: (next: ColumnFilterState | null) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const [q, setQ] = useState('');
  const [excluded, setExcluded] = useState<Set<string>>(() => new Set(state?.kind === 'values' ? state.excluded : state?.kind === 'range' ? (state.excludedGroups ?? []) : []));
  const [min, setMin] = useState(state?.kind === 'range' && state.min !== null ? String(state.min) : '');
  const [max, setMax] = useState(state?.kind === 'range' && state.max !== null ? String(state.max) : '');
  const [includeBlank, setIncludeBlank] = useState(state?.kind === 'range' ? state.includeBlank : true);

  useLayoutEffect(() => {
    const r = anchor.getBoundingClientRect();
    const left = Math.max(8, Math.min(r.left, window.innerWidth - MENU_W - 8));
    setPos({ left, top: r.bottom + 4 });
  }, [anchor]);

  // 外を押す・Esc・画面のスクロールで閉じる
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) && !anchor.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onScroll = (e: Event) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    ref.current?.querySelector<HTMLElement>('button, input')?.focus();
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [anchor, onClose]);

  const shown = useMemo(() => options.filter((o) => !q || o.label.toLowerCase().includes(q.toLowerCase())), [options, q]);
  const allShownChecked = shown.every((o) => !excluded.has(o.key));

  const apply = () => {
    if (filterKind === 'values') onFilter(excluded.size ? { kind: 'values', excluded: [...excluded] } : null);
    if (filterKind === 'range') {
      const toNum = (s: string) => (s.trim() === '' || Number.isNaN(Number(s)) ? null : Number(s));
      const mn = toNum(min);
      const mx = toNum(max);
      const groups = [...excluded];
      onFilter(mn === null && mx === null && includeBlank && !groups.length ? null : { kind: 'range', min: mn, max: mx, includeBlank, ...(groups.length ? { excludedGroups: groups } : {}) });
    }
    onClose();
  };

  return createPortal(
    <div ref={ref} className="dt-menu" style={{ left: pos.left, top: pos.top, width: MENU_W }} role="dialog" aria-label={`${title} の並べ替えと絞り込み`}>
      {sortable && (
        <div className="dt-menu-sort">
          <button type="button" className={`dt-menu-item${sortDir === 'asc' ? ' is-on' : ''}`} onClick={() => (onSort('asc'), onClose())}>
            <IconSortAsc size={14} /> 昇順に並べ替え(小さい順)
          </button>
          <button type="button" className={`dt-menu-item${sortDir === 'desc' ? ' is-on' : ''}`} onClick={() => (onSort('desc'), onClose())}>
            <IconSortDesc size={14} /> 降順に並べ替え(大きい順)
          </button>
          {sortDir && (
            <button type="button" className="dt-menu-item" onClick={() => (onSort(null), onClose())}>
              並べ替えを解除
            </button>
          )}
        </div>
      )}
      {filterKind === 'values' && (
        <div className="dt-menu-filter">
          <input className="ui-input dt-menu-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="検索" aria-label="値を検索" />
          <label className="dt-menu-check">
            <input
              type="checkbox"
              checked={allShownChecked}
              onChange={() =>
                setExcluded((cur) => {
                  const next = new Set(cur);
                  for (const o of shown) (allShownChecked ? next.add(o.key) : next.delete(o.key));
                  return next;
                })
              }
            />
            <span>(すべて選択)</span>
          </label>
          <div className="dt-menu-list">
            {shown.map((o) => (
              <label key={o.key} className="dt-menu-check">
                <input
                  type="checkbox"
                  checked={!excluded.has(o.key)}
                  onChange={() =>
                    setExcluded((cur) => {
                      const next = new Set(cur);
                      if (next.has(o.key)) next.delete(o.key);
                      else next.add(o.key);
                      return next;
                    })
                  }
                />
                <span className="dt-menu-label">{o.label}</span>
                <span className="dt-menu-count">{o.count}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      {filterKind === 'range' && (
        <div className="dt-menu-filter">
          <div className="dt-menu-range">
            <label>
              <span>以上</span>
              <input className="ui-input" inputMode="decimal" value={min} onChange={(e) => setMin(e.target.value)} aria-label="この値以上" />
            </label>
            <label>
              <span>以下</span>
              <input className="ui-input" inputMode="decimal" value={max} onChange={(e) => setMax(e.target.value)} aria-label="この値以下" />
            </label>
          </div>
          <label className="dt-menu-check">
            <input type="checkbox" checked={includeBlank} onChange={(e) => setIncludeBlank(e.target.checked)} />
            <span>空白(—)の行も出す</span>
          </label>
          {options.length > 0 && (
            <div className="dt-menu-list dt-menu-groups">
              {options.map((o) => (
                <label key={o.key} className="dt-menu-check">
                  <input
                    type="checkbox"
                    checked={!excluded.has(o.key)}
                    onChange={() =>
                      setExcluded((cur) => {
                        const next = new Set(cur);
                        if (next.has(o.key)) next.delete(o.key);
                        else next.add(o.key);
                        return next;
                      })
                    }
                  />
                  <span className="dt-menu-label">{o.label}</span>
                  <span className="dt-menu-count">{o.count}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
      {filterKind && (
        <div className="dt-menu-actions">
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => (onFilter(null), onClose())} disabled={!state}>
            絞り込みを解除
          </button>
          <button type="button" className="ui-btn ui-btn-primary ui-btn-sm" onClick={apply}>
            OK
          </button>
        </div>
      )}
    </div>,
    document.body,
  );
}
